/**
 * <order-bump> — one-tap add-on offer above the cart's checkout button
 * (snippets/cart-order-bump.liquid). Adds / removes the offered variant via the
 * Cart AJAX API and re-renders the cart sections the same way cart.js does, so
 * the line items, subtotal and cart icon stay in sync without a page reload.
 * The bump lives outside the re-rendered part of the cart footer, so it keeps
 * its own "added" state, and follows cart updates from elsewhere (e.g. the key
 * removed from the line items).
 */
// Run `fn` once when the animation finishes — or after its duration anyway,
// since paused/hidden tabs may never fire `finish`.
const afterAnimation = (animation, duration, fn) => {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    fn();
  };
  animation.onfinish = run;
  setTimeout(run, duration + 60);
};

class OrderBump extends HTMLElement {
  connectedCallback() {
    this.variantId = Number(this.dataset.variantId);
    this.status = this.querySelector('[data-bump-status]');
    this.querySelector('[data-bump-add]')?.addEventListener('click', () => this.add());
    this.querySelector('[data-bump-remove]')?.addEventListener('click', () => this.remove());

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const items = event.cartData?.items;
        if (Array.isArray(items)) this.setAdded(items.some((item) => item.variant_id === this.variantId));
      });
    }
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  get cartItems() {
    return document.querySelector('cart-items');
  }

  sectionsToRender() {
    return this.cartItems?.getSectionsToRender?.() || [];
  }

  setAdded(added) {
    this.classList.toggle('is-added', added);
    // The pill stays in place as the "added" state; take it out of the tab order.
    const button = this.querySelector('[data-bump-add]');
    if (button) {
      button.toggleAttribute('aria-disabled', added);
      if (added) button.setAttribute('tabindex', '-1');
      else button.removeAttribute('tabindex');
    }
  }

  // Busy is a class (pointer-events: none) plus a flag rather than `disabled`
  // buttons: disabling the focused button drops focus to <body>, which makes
  // mobile browsers jump the page to the top.
  setBusy(busy) {
    this.busy = busy;
    this.classList.toggle('is-busy', busy);
    this.querySelectorAll('button').forEach((button) => button.toggleAttribute('aria-busy', busy));
  }

  // Keep the card where it is on screen while the cart is added/re-rendered (the
  // item list above it changes height), instead of letting the page jump. Mobile
  // browsers can scroll the page up at several moments (layout clamping, focus
  // changes), so rather than fixing one spot we hold the card's position on every
  // scroll event until the shopper touches / scrolls themselves.
  holdPosition() {
    this.releaseHold?.();
    const top = this.getBoundingClientRect().top;
    const x = window.scrollX;
    let frame = 0;
    const restore = () => {
      frame = 0;
      const shift = this.getBoundingClientRect().top - top;
      if (Math.abs(shift) > 1) window.scrollTo({ left: x, top: window.scrollY + shift, behavior: 'instant' });
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(restore);
    };
    const release = () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      ['touchstart', 'wheel', 'keydown'].forEach((type) => window.removeEventListener(type, release));
      this.releaseHold = null;
    };
    window.addEventListener('scroll', schedule, { passive: true });
    ['touchstart', 'wheel', 'keydown'].forEach((type) => window.addEventListener(type, release, { passive: true }));
    this.releaseHold = release;
    return { restore, release };
  }

  // "Roll the dice": a neon 3D die is flicked up from the button, tumbles along a
  // smooth arc and falls back onto it, dissolving into the button, which flares;
  // then the button fills with green into "added". The key is added meanwhile.
  async add() {
    if (this.classList.contains('is-added') || this.busy) return;
    const motion = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (motion) {
      this.classList.add('is-rolling');
      this.rollDice();
    }
    await this.update(`${routes.cart_add_url}`, { items: [{ id: this.variantId, quantity: 1 }] }, true, motion ? 1650 : 0);
    this.classList.remove('is-rolling');
  }

  rollDice() {
    const SIZE = 34;
    const FLIGHT = 1150;
    const box = this.getBoundingClientRect();
    const button = this.querySelector('[data-bump-add]').getBoundingClientRect();
    const midY = button.top - box.top + button.height / 2 - SIZE / 2;
    const from = { x: button.left - box.left + button.width * 0.22 - SIZE / 2, y: midY };
    const to = { x: button.left - box.left + button.width * 0.72 - SIZE / 2, y: midY };
    // Arc height: up above the card's top edge.
    const height = midY + SIZE * 2;

    // Pip positions on a 3×3 grid (1–9, row by row) for faces 1–6.
    const pips = { 1: [5], 2: [1, 9], 3: [1, 5, 9], 4: [1, 3, 7, 9], 5: [1, 3, 5, 7, 9], 6: [1, 3, 4, 6, 7, 9] };
    // Face on each side of the cube, and the cube rotation [x, y] that brings it to the front.
    const sides = { front: 1, bottom: 2, right: 3, left: 4, top: 5, back: 6 };
    const facing = { 1: [0, 0], 2: [90, 0], 3: [0, -90], 4: [0, 90], 5: [-90, 0], 6: [0, 180] };

    const die = document.createElement('span');
    die.className = 'order-bump__dice';
    die.setAttribute('aria-hidden', 'true');
    const cube = document.createElement('span');
    cube.className = 'order-bump__cube';
    Object.entries(sides).forEach(([side, value]) => {
      const face = document.createElement('span');
      face.className = `order-bump__face order-bump__face--${side}`;
      pips[value].forEach((cell) => {
        const pip = document.createElement('i');
        pip.style.gridRow = String(Math.ceil(cell / 3));
        pip.style.gridColumn = String(((cell - 1) % 3) + 1);
        face.appendChild(pip);
      });
      cube.appendChild(face);
    });
    die.appendChild(cube);
    this.appendChild(die);

    // A true parabola (uniform horizontal speed, gravity-like vertical), sampled
    // densely so the motion stays smooth; slightly larger near the top.
    const steps = 24;
    const frames = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = from.x + (to.x - from.x) * t;
      const y = from.y - height * 4 * t * (1 - t);
      const scale = 0.7 + 0.5 * Math.sin(Math.PI * t) + (t === 0 ? -0.2 : 0);
      frames.push({ transform: `translate(${x}px, ${y}px) scale(${scale})`, opacity: t === 0 ? 0 : 1, offset: t });
    }
    die.animate(frames, { duration: FLIGHT, easing: 'linear', fill: 'forwards' });

    const face = 1 + Math.floor(Math.random() * 6);
    const [fx, fy] = facing[face];
    cube.animate(
      [
        { transform: 'rotateX(0deg) rotateY(0deg) rotateZ(0deg)' },
        { transform: `rotateX(${fx + 720}deg) rotateY(${fy + 540}deg) rotateZ(0deg)` },
      ],
      { duration: FLIGHT, easing: 'cubic-bezier(0.25, 0.55, 0.35, 1)', fill: 'forwards' }
    );

    // Landed on the button: the die dissolves into it in neon and the button flares.
    setTimeout(() => {
      this.classList.remove('is-charging');
      void this.offsetWidth;
      this.classList.add('is-charging');
      setTimeout(() => this.classList.remove('is-charging'), 800);

      const fade = cube.animate(
        [
          { transform: `rotateX(${fx + 720}deg) rotateY(${fy + 540}deg) scale(1)`, opacity: 1, filter: 'brightness(1) blur(0)' },
          { transform: `rotateX(${fx + 720}deg) rotateY(${fy + 540}deg) scale(0.35)`, opacity: 0, filter: 'brightness(2.4) blur(0.5rem)' },
        ],
        { duration: 380, easing: 'cubic-bezier(0.4, 0, 0.7, 0.2)', fill: 'forwards' }
      );
      afterAnimation(fade, 380, () => die.remove());
    }, FLIGHT);
  }

  async remove() {
    if (this.busy) return;
    this.setBusy(true);
    try {
      const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
      const line = cart.items.find((item) => item.variant_id === this.variantId);
      if (!line) {
        this.setAdded(false);
        this.setBusy(false);
        return;
      }
      await this.update(`${routes.cart_change_url}`, { id: line.key, quantity: 0 }, false);
    } catch (error) {
      console.error('order-bump remove failed', error);
      this.setBusy(false);
    }
  }

  async update(url, payload, added, minDuration = 0) {
    this.setBusy(true);
    const hold = this.holdPosition();
    setTimeout(hold.release, minDuration + 2500);
    const sections = this.sectionsToRender();
    const started = performance.now();
    try {
      const response = await fetch(url, {
        ...fetchConfig(),
        body: JSON.stringify({
          ...payload,
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (state.status || state.errors) throw new Error(state.description || state.message || 'cart error');

      // Let the animation finish before the state flips.
      const wait = minDuration - (performance.now() - started);
      if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));

      this.setAdded(added);
      this.classList.remove('is-rolling');
      this.renderSections(state.sections, sections);
      hold?.restore();
      // Done from the shopper's point of view: un-dim now, not after the
      // follow-up cart fetch below (that gap made the card flash darker).
      this.setBusy(false);
      if (this.status) this.status.textContent = this.querySelector(added ? '.order-bump__label--added' : '.order-bump__label--add')?.textContent.trim() || '';

      // Let the rest of the page know. Sections are already rendered, so use the
      // cart's own source to stop cart.js from re-fetching them.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
      }
      return true;
    } catch (error) {
      console.error('order-bump update failed', error);
      hold.release();
      return false;
    } finally {
      this.setBusy(false);
    }
  }

  renderSections(rendered, sections) {
    const cartItems = this.cartItems;
    if (!rendered || !cartItems) {
      window.location.reload();
      return;
    }
    sections.forEach((section) => {
      const host = document.getElementById(section.id);
      if (!host || !rendered[section.section]) return;
      const target = host.querySelector(section.selector) || host;
      target.innerHTML = cartItems.getSectionInnerHTML(rendered[section.section], section.selector);
    });
  }
}

if (!customElements.get('order-bump')) customElements.define('order-bump', OrderBump);
